import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Chart } from 'primereact/chart';
import { getByDateRange } from '../../services/pdataService';

// ---------------------------------------------------------------------------
// Constants & helpers
// ---------------------------------------------------------------------------
const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Defaults for the widget. Anything passed via the `config` prop overrides these.
const DEFAULT_CONFIG = {
  title: 'Weight Monitor',
  author: '',
  type: 'weight',
  target_weight: '',
  target_date: '',
};

// A saved goal is stored per entry type so several widgets don't collide
const goalKeyFor = (type) => `weightGoal:${type}`;

// Accepts ISO dates ("2026-12-31") and day-first dates ("31/12/2026", "31-12-2026", "31.12.2026")
const parseConfigDate = (v) => {
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const str = String(v ?? '').trim();
  if (!str) return null;
  const dmy = str.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  const d = dmy ? new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1])) : new Date(str);
  return Number.isNaN(d.getTime()) ? null : d;
};

// What the two inputs should show for a given config (each is filled independently)
const inputsFromConfig = (cfg) => {
  const w = Number(cfg.target_weight);
  const weight = String(cfg.target_weight ?? '').trim() !== '' && Number.isFinite(w) && w > 0 ? String(w) : '';
  return { weight, date: parseConfigDate(cfg.target_date) };
};

// Builds a goal from config.target_weight / config.target_date, or null unless both are valid
const goalFromConfig = (cfg) => {
  const { weight, date } = inputsFromConfig(cfg);
  if (!weight || !date) return null;
  const d = new Date(date);
  d.setHours(12, 0, 0, 0);
  return { weight: Number(weight), date: d.toISOString() };
};

// Horizons to forecast, in days
const FORECAST_HORIZONS = [
  { label: '1 month', days: 30 },
  { label: '3 months', days: 91 },
  { label: '6 months', days: 182 },
  { label: '1 year', days: 365 },
];

const round1 = (n) => Math.round(n * 10) / 10;
const fmt = (n) => `${n > 0 ? '+' : ''}${n}`;

const fmtDay = (d) =>
  new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

const fmtShort = (d) =>
  new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });

// Normalises raw entries ({ createdAt, value: { $numberDecimal } }) to { t, weight }
const normalise = (entries) =>
  (entries || [])
    .map((e) => ({
      t: new Date(e.createdAt).getTime(),
      weight: parseFloat(e?.value?.$numberDecimal ?? e?.value),
    }))
    .filter((p) => Number.isFinite(p.t) && Number.isFinite(p.weight))
    .sort((a, b) => a.t - b.t);

/**
 * Least-squares line through the points.
 * x = days since the first entry, y = weight.
 */
function fitTrend(points) {
  const first = points[0];
  const xs = points.map((p) => (p.t - first.t) / MS_PER_DAY);
  const ys = points.map((p) => p.weight);
  const n = points.length;
  const meanX = xs.reduce((a, b) => a + b, 0) / n;
  const meanY = ys.reduce((a, b) => a + b, 0) / n;

  let sxx = 0;
  let sxy = 0;
  let syy = 0;
  for (let i = 0; i < n; i++) {
    const dx = xs[i] - meanX;
    const dy = ys[i] - meanY;
    sxx += dx * dx;
    sxy += dx * dy;
    syy += dy * dy;
  }

  const slope = sxx === 0 ? 0 : sxy / sxx; // lbs per day
  const intercept = meanY - slope * meanX;
  const r2 = syy === 0 ? 1 : (sxy * sxy) / (sxx * syy);
  return { first, xs, slope, intercept, r2 };
}

// ---------------------------------------------------------------------------
// Summary & general forecast (1, 3, 6, 12 months)
// ---------------------------------------------------------------------------
function summariseAndPredictWeight(entries, now = new Date()) {
  const points = normalise(entries);
  if (points.length === 0) return null;

  const first = points[0];
  const latest = points[points.length - 1];
  const weights = points.map((p) => p.weight);
  const spanDays = (latest.t - first.t) / MS_PER_DAY;

  const summary = {
    count: points.length,
    spanDays: round1(spanDays),
    firstWeight: round1(first.weight),
    latestWeight: round1(latest.weight),
    min: round1(Math.min(...weights)),
    max: round1(Math.max(...weights)),
    average: round1(weights.reduce((a, b) => a + b, 0) / weights.length),
    totalChange: round1(latest.weight - first.weight),
  };

  if (points.length < 2 || spanDays < 1) {
    return { summary, trend: null, predictions: [], confidence: 'none' };
  }

  const n = points.length;
  const { slope, intercept, r2 } = fitTrend(points);

  let confidence = 'low';
  if (n >= 10 && spanDays >= 21 && r2 >= 0.5) confidence = 'high';
  else if (n >= 5 && spanDays >= 7 && r2 >= 0.25) confidence = 'medium';

  const todayX = (now.getTime() - first.t) / MS_PER_DAY;
  const predictions = FORECAST_HORIZONS.map(({ label, days }) => {
    const predicted = intercept + slope * (todayX + days);
    return {
      label,
      days,
      weight: round1(Math.max(predicted, 0)),
      change: round1(predicted - latest.weight),
    };
  });

  return {
    summary,
    trend: {
      perDay: Math.round(slope * 100) / 100,
      perWeek: round1(slope * 7),
      perMonth: round1(slope * 30),
      r2: Math.round(r2 * 100) / 100,
    },
    predictions,
    confidence,
  };
}

// ---------------------------------------------------------------------------
// Goal analysis: "will I hit my target weight by my target date?"
// ---------------------------------------------------------------------------
function analyseGoal(entries, goal, now = new Date()) {
  if (!goal) return null;

  const points = normalise(entries);
  if (points.length < 2) return { status: 'nodata' };

  const first = points[0];
  const latest = points[points.length - 1];
  if ((latest.t - first.t) / MS_PER_DAY < 1) return { status: 'nodata' };

  const { slope, intercept } = fitTrend(points);
  const todayX = (now.getTime() - first.t) / MS_PER_DAY;
  const targetX = (new Date(goal.date).getTime() - first.t) / MS_PER_DAY;
  const daysLeft = targetX - todayX;

  const goingDown = goal.weight < first.weight;
  const reached = goingDown ? latest.weight <= goal.weight : latest.weight >= goal.weight;

  if (reached) return { status: 'reached', latest: round1(latest.weight) };
  if (daysLeft <= 0) {
    return { status: 'past', latest: round1(latest.weight), gap: round1(latest.weight - goal.weight) };
  }

  const trendToday = intercept + slope * todayX;
  const projected = intercept + slope * targetX;
  const onTrack = goingDown ? projected <= goal.weight : projected >= goal.weight;

  // Rate needed from the latest weigh-in to land on the target on the target date
  const requiredPerWeek = ((goal.weight - latest.weight) / daysLeft) * 7;

  // When would the current trend reach the target weight?
  const movingToward = goingDown ? slope < 0 : slope > 0;
  let etaDate = null;
  if (movingToward) {
    const etaDays = Math.max((goal.weight - trendToday) / slope, 0);
    if (etaDays < 3650) etaDate = new Date(now.getTime() + etaDays * MS_PER_DAY);
  }

  return {
    status: onTrack ? 'on-track' : 'off-track',
    latest: round1(latest.weight),
    projected: round1(projected),
    gapAtTarget: round1(projected - goal.weight), // + means above target
    daysLeft: Math.ceil(daysLeft),
    requiredPerWeek: round1(requiredPerWeek),
    currentPerWeek: round1(slope * 7),
    movingToward,
    etaDate,
  };
}

// ---------------------------------------------------------------------------
// Chart: actual weigh-ins vs trend line vs the straight path to your goal
// ---------------------------------------------------------------------------
function buildChart(entries, goal, now = new Date()) {
  const points = normalise(entries);
  if (points.length === 0) return null;

  const first = points[0];
  const latest = points[points.length - 1];
  const toX = (t) => (t - first.t) / MS_PER_DAY;
  const latestX = toX(latest.t);
  const targetX = goal ? toX(new Date(goal.date).getTime()) : null;

  const css = getComputedStyle(document.documentElement);
  const textColor = css.getPropertyValue('--text-color').trim() || '#495057';
  const mutedColor = css.getPropertyValue('--text-color-secondary').trim() || '#6c757d';
  const gridColor = css.getPropertyValue('--surface-border').trim() || '#dfe7ef';

  const datasets = [
    {
      label: 'Actual weight',
      data: points.map((p) => ({ x: toX(p.t), y: p.weight })),
      borderColor: '#3b82f6',
      backgroundColor: '#3b82f6',
      pointRadius: 3,
      borderWidth: 2,
      tension: 0.2,
      showLine: true,
      order: 2,
    },
  ];

  const spanDays = latestX;
  if (points.length >= 2 && spanDays >= 1) {
    const { slope, intercept } = fitTrend(points);
    const endX = Math.max(targetX ?? 0, latestX + 30);
    datasets.push({
      label: 'Trend (where you\'re heading)',
      data: [
        { x: 0, y: Math.max(intercept, 0) },
        { x: endX, y: Math.max(intercept + slope * endX, 0) },
      ],
      borderColor: '#f59e0b',
      backgroundColor: '#f59e0b',
      borderDash: [6, 4],
      borderWidth: 2,
      pointRadius: 0,
      showLine: true,
      order: 3,
    });
  }

  if (goal) {
    datasets.push({
      label: 'Path to goal',
      data: [
        { x: latestX, y: latest.weight },
        { x: targetX, y: goal.weight },
      ],
      borderColor: '#22c55e',
      backgroundColor: '#22c55e',
      borderWidth: 2,
      pointRadius: [0, 0],
      showLine: true,
      order: 4,
    });
    datasets.push({
      label: 'Goal',
      data: [{ x: targetX, y: goal.weight }],
      borderColor: '#22c55e',
      backgroundColor: '#22c55e',
      pointStyle: 'rectRot',
      pointRadius: 8,
      showLine: false,
      order: 1,
    });
  }

  const options = {
    maintainAspectRatio: false,
    interaction: { mode: 'nearest', intersect: true },
    plugins: {
      legend: { labels: { color: textColor, usePointStyle: true } },
      tooltip: {
        callbacks: {
          title: (items) => fmtDay(first.t + items[0].parsed.x * MS_PER_DAY),
          label: (ctx) => `${ctx.dataset.label}: ${round1(ctx.parsed.y)} lbs`,
        },
      },
    },
    scales: {
      x: {
        type: 'linear',
        ticks: {
          color: mutedColor,
          maxTicksLimit: 6,
          callback: (v) => fmtShort(first.t + v * MS_PER_DAY),
        },
        grid: { color: gridColor },
      },
      y: {
        ticks: { color: mutedColor, callback: (v) => `${v}` },
        title: { display: true, text: 'lbs', color: mutedColor },
        grid: { color: gridColor },
      },
    },
  };

  return { data: { datasets }, options };
}

// ---------------------------------------------------------------------------
// UI pieces
// ---------------------------------------------------------------------------
const CONFIDENCE_TEXT = {
  none: 'Add entries on at least two different days to get a forecast.',
  low: 'Low confidence: not much data, or your weight is fluctuating a lot.',
  medium: 'Medium confidence: a reasonable trend, but it may change.',
  high: 'High confidence: a consistent trend across plenty of data.',
};

function WeightForecast({ forecast }) {
  if (!forecast) return null;
  const { summary, trend, predictions, confidence } = forecast;

  return (
    <div className="flex flex-column gap-2 mt-3">
      <strong>Summary (all entries)</strong>
      <div>
        {summary.count} entries over {Math.round(summary.spanDays)} days. Started at{' '}
        {summary.firstWeight} lbs, now {summary.latestWeight} lbs ({fmt(summary.totalChange)} lbs).
        Average {summary.average} lbs, low {summary.min}, high {summary.max}.
      </div>

      {trend && (
        <div>
          Trend: {fmt(trend.perWeek)} lbs/week ({fmt(trend.perMonth)} lbs/month)
        </div>
      )}

      {predictions.length > 0 && (
        <>
          <strong>Predicted weight</strong>
          <table className="p-datatable p-component" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>In</th>
                <th>Weight</th>
                <th>Change from now</th>
              </tr>
            </thead>
            <tbody>
              {predictions.map((p) => (
                <tr key={p.label}>
                  <td>{p.label}</td>
                  <td><b>{p.weight} lbs</b></td>
                  <td>{fmt(p.change)} lbs</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      <small>{CONFIDENCE_TEXT[confidence]} Predictions assume your current trend continues.</small>
    </div>
  );
}

function GoalResult({ goal, analysis }) {
  if (!goal || !analysis) return null;

  const dateText = fmtDay(goal.date);
  let headline;
  let detail = null;
  let color = 'inherit';

  switch (analysis.status) {
    case 'nodata':
      return <small>Add entries on at least two different days to see if you are on track.</small>;
    case 'reached':
      headline = `You've reached your goal of ${goal.weight} lbs.`;
      color = '#16a34a';
      break;
    case 'past':
      headline = `The target date (${dateText}) has passed.`;
      detail = `You're at ${analysis.latest} lbs, ${Math.abs(analysis.gap)} lbs ${
        analysis.gap > 0 ? 'above' : 'below'
      } your ${goal.weight} lbs target. Set a new date to keep going.`;
      color = '#d97706';
      break;
    case 'on-track':
      headline = `On track. Your trend puts you at ${analysis.projected} lbs on ${dateText}.`;
      detail = `That's ${Math.abs(analysis.gapAtTarget)} lbs ${
        analysis.gapAtTarget === 0 ? 'on' : 'ahead of'
      } your ${goal.weight} lbs target, with ${analysis.daysLeft} days to go.`;
      color = '#16a34a';
      break;
    default: // off-track
      headline = `Off track. Your trend puts you at ${analysis.projected} lbs on ${dateText}.`;
      detail = `That's ${Math.abs(analysis.gapAtTarget)} lbs ${
        analysis.gapAtTarget > 0 ? 'above' : 'below'
      } your ${goal.weight} lbs target. You need about ${fmt(analysis.requiredPerWeek)} lbs/week; your trend is ${fmt(
        analysis.currentPerWeek
      )} lbs/week.`;
      color = '#dc2626';
  }

  return (
    <div className="flex flex-column gap-1">
      <strong style={{ color }}>{headline}</strong>
      {detail && <div>{detail}</div>}
      {(analysis.status === 'on-track' || analysis.status === 'off-track') &&
        (analysis.etaDate ? (
          <small>At your current rate you'd reach {goal.weight} lbs around {fmtDay(analysis.etaDate)}.</small>
        ) : (
          <small>Your current trend is not moving toward {goal.weight} lbs.</small>
        ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Goal persistence (browser localStorage; falls back to in-memory only)
// ---------------------------------------------------------------------------
const loadGoal = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const g = JSON.parse(raw);
    if (Number.isFinite(g.weight) && g.date && !Number.isNaN(new Date(g.date).getTime())) return g;
  } catch (e) {
    /* ignore */
  }
  return null;
};

const saveGoal = (key, goal) => {
  try {
    if (goal) localStorage.setItem(key, JSON.stringify(goal));
    else localStorage.removeItem(key);
  } catch (e) {
    /* ignore */
  }
};

// Decides what the goal and the two inputs start as: the user's saved goal wins,
// otherwise the config values (inputs are filled even if only one of them is set)
const resolveGoalState = (goalKey, cfg) => {
  const saved = loadGoal(goalKey);
  if (saved) {
    return { goal: saved, weightInput: String(saved.weight), dateInput: new Date(saved.date) };
  }
  const { weight, date } = inputsFromConfig(cfg);
  return { goal: goalFromConfig(cfg), weightInput: weight, dateInput: date };
};

// ---------------------------------------------------------------------------
// Main widget
// ---------------------------------------------------------------------------
/**
 * Props (as passed by the dashboard grid):
 *  - config: { title, author, type, target_weight, target_date } merged over the registry's defaultConfig
 *  - title:  the layout item's title (wins over config.title when set)
 *  - id:     the layout item's id (keeps each instance's saved goal separate)
 *  - type:   the widget type from the layout item (used if config.type is empty)
 */
const TestWidget = ({ config = {}, title, id, type }) => {
  const cfg = { ...DEFAULT_CONFIG, ...config };
  const heading = title || cfg.title;
  const entryType = cfg.type || type;
  const goalKey = goalKeyFor(id ?? entryType);

  const [allEntries, setAllEntries] = useState([]);

  // Goal: the saved goal + the form's draft values (pre-filled from config)
  const [initial] = useState(() => resolveGoalState(goalKey, cfg));
  const [goal, setGoal] = useState(initial.goal);
  const [goalWeightInput, setGoalWeightInput] = useState(initial.weightInput);
  const [goalDateInput, setGoalDateInput] = useState(initial.dateInput);
  const [goalError, setGoalError] = useState('');

  // If the dashboard config changes later, re-fill the inputs from it,
  // unless the user has saved their own goal in this widget
  useEffect(() => {
    if (loadGoal(goalKey)) return;
    const r = resolveGoalState(goalKey, cfg);
    setGoal(r.goal);
    setGoalWeightInput(r.weightInput);
    setGoalDateInput(r.dateInput);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalKey, cfg.target_weight, cfg.target_date]);

  const validateWeight = (v) => {
    if (!v || !v.toString().trim()) return 'Weight is required';
    if (isNaN(Number(v))) return 'Weight must be a number';
    if (Number(v) <= 0) return 'Weight must be greater than 0';
    return '';
  };

  // Fetch every available weight; forecast, goal and chart are all derived from this
  const getAllData = useCallback(async () => {
    try {
      const tomorrow = new Date(Date.now() + MS_PER_DAY);
      const data = await getByDateRange('2000-01-01', tomorrow.toISOString().split('T')[0]);
      setAllEntries(data.pdatas || []);
    } catch (error) {
      console.error('Error fetching all weight data:', error);
    }
  }, []);

  useEffect(() => {
    getAllData();
  }, [getAllData]);

  // Derived data (recomputed when entries or the goal change; no refetch needed)
  const forecast = useMemo(() => summariseAndPredictWeight(allEntries), [allEntries]);
  const goalAnalysis = useMemo(() => analyseGoal(allEntries, goal), [allEntries, goal]);
  const chart = useMemo(() => buildChart(allEntries, goal), [allEntries, goal]);

  const handleSetGoal = () => {
    const wErr = validateWeight(goalWeightInput);
    if (wErr) {
      setGoalError(wErr.replace('Weight', 'Goal weight'));
      return;
    }
    if (!goalDateInput) {
      setGoalError('Pick a target date');
      return;
    }
    const date = new Date(goalDateInput);
    date.setHours(12, 0, 0, 0);
    if (date.getTime() <= Date.now()) {
      setGoalError('Target date must be in the future');
      return;
    }
    setGoalError('');
    const next = { weight: Number(goalWeightInput), date: date.toISOString() };
    setGoal(next);
    saveGoal(goalKey, next);
  };

  const handleClearGoal = () => {
    setGoal(null);
    saveGoal(goalKey, null);
    setGoalWeightInput('');
    setGoalDateInput(null);
    setGoalError('');
  };

  const tomorrow = new Date(Date.now() + MS_PER_DAY);

  return (
    <Card className="w-full h-full overflow-auto" title={heading} subTitle={cfg.author ? `by ${cfg.author}` : undefined}>
      <div className="flex flex-column gap-3">
        {/* Goal */}
        <div className="flex flex-column gap-2">
          <strong>Goal</strong>
          <div className="flex flex-wrap align-items-start gap-2">
            <InputText
              type="number"
              value={goalWeightInput}
              onChange={(e) => setGoalWeightInput(e.target.value)}
              placeholder="Target weight (lbs)"
              aria-label="Target weight in pounds"
            />
            <Calendar
              value={goalDateInput}
              onChange={(e) => setGoalDateInput(e.value)}
              minDate={tomorrow}
              dateFormat="dd M yy"
              placeholder="Target date"
              showIcon
              aria-label="Target date"
            />
            <Button label={goal ? 'Update goal' : 'Set goal'} icon="pi pi-flag" onClick={handleSetGoal} />
            {goal && (
              <Button
                label="Clear"
                icon="pi pi-times"
                severity="secondary"
                outlined
                onClick={handleClearGoal}
              />
            )}
          </div>
          {goalError && <small className="p-error">{goalError}</small>}
          <GoalResult goal={goal} analysis={goalAnalysis} />
        </div>

        {/* Chart: actual vs trend vs path to goal */}
        {chart && (
          <div>
            <Chart type="scatter" data={chart.data} options={chart.options} style={{ height: '20rem' }} />
          </div>
        )}

        {/* General forecast */}
        <div className="flex flex-column gap-2">
          <WeightForecast forecast={forecast} />
        </div>
      </div>
    </Card>
  );
};

export default TestWidget;
